import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axiosInstance from 'shared/api/axiosInstance';
import { endpoints } from 'shared/api/endPoints';
import Header from 'shared/components/Header';
import ClockLoader from 'shared/components/ClockLoader';
import useGroupService from 'shared/api/services/groupService';
import { useAuth } from 'shared/hooks/useAuth';
import { useTenantPath } from 'shared/hooks/useTenantPath';
import {
  PERMISSION_ACTIVITY_ORDER,
  PERMISSION_OPTIONS,
  comparePermissionsByActivity,
  resolvePermissionActivity,
  resolvePermissionServiceCode,
  resolveServiceLabel,
} from 'platform/rbac/Permissions';

const unwrapRecords = (axiosResponse) => {
  const records = axiosResponse?.data?.data;
  if (!records || typeof records !== 'object') {
    return { rows: [] };
  }
  return {
    rows: Array.isArray(records.data) ? records.data : [],
  };
};

const setsEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
};

const GroupPermission = () => {
  const navigate = useNavigate();
  const { tp } = useTenantPath();
  const { groupId } = useParams();
  const { getGroup, getGroupPermissionIds, setGroupPermissions } = useGroupService();
  const { user } = useAuth();
  const profile = user?.data;

  const [groupName, setGroupName] = useState('');
  const [groupServiceCode, setGroupServiceCode] = useState('');
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const isDirty = useMemo(
    () => !setsEqual(selectedIds, savedIds),
    [selectedIds, savedIds]
  );

  const assignUsersPath = groupId ? `/groups/${groupId}/users-assigned` : '/groups';

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [group, ids, permRes] = await Promise.all([
        getGroup(groupId),
        getGroupPermissionIds(groupId),
        axiosInstance.get(`${endpoints.permissions.list}?page=0&size=500`),
      ]);
      setGroupName(group?.name ?? 'Group');
      setGroupServiceCode(
        group?.serviceCode != null && String(group.serviceCode).trim() !== ''
          ? String(group.serviceCode).trim()
          : ''
      );
      const nextIds = new Set(ids.map(String));
      setSelectedIds(nextIds);
      setSavedIds(new Set(nextIds));
      const { rows } = unwrapRecords(permRes);
      setAllPermissions(rows);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to load group permissions';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load');
      navigate(tp('/groups'));
    } finally {
      setLoading(false);
    }
  }, [groupId, getGroup, getGroupPermissionIds, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!groupId) return false;
    setSaving(true);
    try {
      const ids = Array.from(selectedIds);
      await setGroupPermissions(groupId, ids);
      setSavedIds(new Set(ids));
      toast.success('Permissions saved');
      return true;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to save permissions';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goToAssignUsers = () => {
    navigate(assignUsersPath);
  };

  const handleAssignUsersClick = () => {
    if (!groupId || loading) return;
    if (isDirty) {
      setLeaveDialogOpen(true);
      return;
    }
    goToAssignUsers();
  };

  const handleLeaveWithoutSaving = () => {
    setLeaveDialogOpen(false);
    goToAssignUsers();
  };

  const handleSaveAndContinue = async () => {
    const ok = await handleSave();
    if (!ok) return;
    setLeaveDialogOpen(false);
    goToAssignUsers();
  };

  const sorted = useMemo(
    () =>
      [...allPermissions].sort((a, b) =>
        comparePermissionsByActivity(a.permission, b.permission)
      ),
    [allPermissions]
  );
  const subscribedServiceCodes = useMemo(
    () =>
      Array.isArray(profile?.serviceCodes)
        ? profile.serviceCodes
            .map((code) => String(code).trim())
            .filter((code) => code !== '')
        : [],
    [profile?.serviceCodes]
  );
  const permissionMeta = useCallback((permissionKey) => {
    const normalized = String(permissionKey ?? '').trim().toUpperCase();
    return PERMISSION_OPTIONS.find((option) => option.permission === normalized) || null;
  }, []);

  const visiblePermissions = useMemo(
    () =>
      sorted.filter((permission) => {
        const serviceCode =
          (permission?.serviceCode != null && String(permission.serviceCode).trim() !== '')
            ? String(permission.serviceCode).trim()
            : resolvePermissionServiceCode(permission?.permission);
        if (!serviceCode) return true;
        if (groupServiceCode) {
          return serviceCode === groupServiceCode;
        }
        // No group service yet — show permissions for services this tenant subscribes to.
        if (subscribedServiceCodes.length === 0) return true;
        return subscribedServiceCodes.includes(serviceCode);
      }),
    [groupServiceCode, sorted, subscribedServiceCodes]
  );

  const serviceHeading = useMemo(() => {
    if (groupServiceCode) {
      return resolveServiceLabel(groupServiceCode) || groupServiceCode;
    }
    return null;
  }, [groupServiceCode]);

  const permissionGroups = useMemo(() => {
    const buckets = new Map();
    PERMISSION_ACTIVITY_ORDER.forEach((activity) => buckets.set(activity, []));
    visiblePermissions.forEach((permission) => {
      const activity = resolvePermissionActivity(permission?.permission);
      if (!buckets.has(activity)) buckets.set(activity, []);
      buckets.get(activity).push(permission);
    });
    return PERMISSION_ACTIVITY_ORDER
      .filter((activity) => (buckets.get(activity) || []).length > 0)
      .map((activity) => ({
        activity,
        items: buckets.get(activity),
      }));
  }, [visiblePermissions]);

  const toggleGroup = (items, selectAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.forEach((permission) => {
        const id = String(permission.id);
        if (selectAll) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  return (
    <Box m="20px">
      <Header
        title="Group permissions"
        // subtitle={groupName ? `Assign permissions for “${groupName}”` : 'Loading…'}
      />
      <Box mt={2} mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving || loading || !isDirty}
        >
          {saving ? <ClockLoader inline size={22} /> : 'Save permissions'}
        </Button>
        <Button
          variant="outlined"
          onClick={handleAssignUsersClick}
          disabled={loading || !groupId || saving}
          sx={{ textTransform: 'none' }}
        >
          Assign users to Group
        </Button>
        {isDirty ? (
          <Typography variant="caption" color="warning.main">
            Unsaved changes
          </Typography>
        ) : null}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <ClockLoader size={48} />
        </Box>
      ) : (
        <Paper elevation={0} variant="outlined" sx={{ p: 2, maxWidth: 820 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Select permissions for this group ({selectedIds.size} selected)
            {groupName ? ` — ${groupName}` : ''}
            {serviceHeading ? ` · ${serviceHeading}` : ''}
          </Typography>
          {visiblePermissions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {groupServiceCode
                ? `No permissions are available for ${serviceHeading || 'this service'} yet. Open Permissions to add them, or refresh after the catalog sync.`
                : 'No permissions are available. Assign a service to this group, or ensure the tenant is subscribed to a service.'}
            </Typography>
          ) : (
            permissionGroups.map((group, index) => {
              const selectedInGroup = group.items.filter((item) =>
                selectedIds.has(String(item.id))
              ).length;
              const allSelected = selectedInGroup === group.items.length && group.items.length > 0;
              const someSelected = selectedInGroup > 0 && !allSelected;
              return (
                <Box key={group.activity} sx={{ mt: index === 0 ? 1 : 2.5 }}>
                  {index > 0 ? <Divider sx={{ mb: 1.5 }} /> : null}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    mb={0.5}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {group.activity}
                    </Typography>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={() => toggleGroup(group.items, !allSelected)}
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          {allSelected ? 'Clear group' : 'Select group'}
                        </Typography>
                      }
                    />
                  </Box>
                  <FormGroup sx={{ pl: { xs: 0.5, sm: 1 } }}>
                    {group.items.map((p) => {
                      const id = String(p.id);
                      const meta = permissionMeta(p.permission);
                      const label = meta?.label || p.description || p.permission || id;
                      const code = p.permission && label !== p.permission ? p.permission : null;
                      return (
                        <FormControlLabel
                          key={id}
                          control={
                            <Checkbox
                              checked={selectedIds.has(id)}
                              onChange={() => toggle(id)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" component="span">
                              {label}
                              {code ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {code}
                                </Typography>
                              ) : p.description && p.description !== label ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {p.description}
                                </Typography>
                              ) : null}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              );
            })
          )}
        </Paper>
      )}

      <Dialog
        open={leaveDialogOpen}
        onClose={() => !saving && setLeaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save changes before continuing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved permission changes for this group. Save them before
            assigning users, or leave without saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setLeaveDialogOpen(false)}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLeaveWithoutSaving}
            disabled={saving}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Leave without saving
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            disabled={saving}
            variant="contained"
            color="secondary"
            sx={{ textTransform: 'none' }}
          >
            {saving ? <ClockLoader inline size={22} /> : 'Save and continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupPermission;
